/* eslint-disable lines-between-class-members */
import * as THREE from "three";
import { logicalChildrenOf, ScriptableMixin, ScriptableObject, ScriptableObjects } from "./ScriptableObjects.js";

/* ────────────────────────────────────────────────────────────────────────── */
/* Environment & helpers                                                      */
/* ────────────────────────────────────────────────────────────────────────── */
const HAS_AFRAME = typeof window !== "undefined" && !!window.AFRAME;
// eslint-disable-next-line no-undef
const SnapColliderCtor =
  (typeof globalThis !== "undefined" && (globalThis.SnapColliderNode || globalThis.SnapCollider)) || undefined;

const isAFrameEntity = v => !!(v && typeof v.addEventListener === "function" && v.object3D);
const isObject3D = v => !!(v && (v.isObject3D || (typeof THREE !== "undefined" && v instanceof THREE.Object3D)));

/* ────────────────────────────────────────────────────────────────────────── */
/* Public API: properties                                                     */
/* ────────────────────────────────────────────────────────────────────────── */
export const SNAP_PROPS = ["snapInCenter"];

/* ────────────────────────────────────────────────────────────────────────── */
/* Editor / plain-Three adapter                                               */
/* ────────────────────────────────────────────────────────────────────────── */
class SnapColliderEditorAdapter {
  constructor(sourceObject, originalDescriptors = new Map()) {
    this.sourceObject = sourceObject;
    this._values = new Map();
    this._originalDescriptors = originalDescriptors;

    SNAP_PROPS.forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => this._values.get(prop),
        set: v => {
          this._values.set(prop, v);
          const desc = this._originalDescriptors.get(prop);
          if (desc?.set) {
            try {
              desc.set.call(sourceObject, v);
            } catch {
              /* ignore */
            }
          } else {
            try {
              // For SnapColliderNode, the property might be stored with an underscore
              if (`_${prop}` in sourceObject) {
                sourceObject[`_${prop}`] = v;
              } else {
                sourceObject[prop] = v;
              }
            } catch {
              /* ignore */
            }
          }
        }
      });

      // seed initial value (best-effort, avoid triggering setters)
      const d = originalDescriptors.get(prop);
      let initial;
      try {
        if (d?.get) initial = d.get.call(sourceObject);
        else if (Object.prototype.hasOwnProperty.call(sourceObject, prop)) initial = sourceObject[prop];
        // Check for underscore version for SnapColliderNode
        else if (Object.prototype.hasOwnProperty.call(sourceObject, `_${prop}`)) initial = sourceObject[`_${prop}`];
      } catch {
        /* ignore */
      }
      this._values.set(prop, initial);
    });
  }

  dispose() {
    this._values.clear();
    this.sourceObject = null;
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* A-Frame adapter (PRESERVED ECS LOGIC)                                      */
/* ────────────────────────────────────────────────────────────────────────── */
class SnapColliderAFrameAdapter {
  constructor(sourceObject) {
    this.sourceObject = sourceObject;

    // snapInCenter is a flag for editor behaviour and doesn't map to a component
    // We store it on the adapter for clone operations - CRITICAL ECS LOGIC PRESERVED
    Object.defineProperty(this, "snapInCenter", {
      get: () => !!this._snapInCenterFlag,
      set: v => {
        this._snapInCenterFlag = !!v;
      }
    });
  }

  dispose() {
    this.sourceObject = null;
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Lazy adapter bootstrap (works for adopted instances)                       */
/* ────────────────────────────────────────────────────────────────────────── */
function collectDescriptorsFor(obj) {
  const map = new Map();
  for (const prop of SNAP_PROPS) {
    let cur = obj;
    while (cur) {
      const d = Object.getOwnPropertyDescriptor(cur, prop);
      if (d) {
        map.set(prop, d);
        break;
      }
      cur = Object.getPrototypeOf(cur);
    }
    if (!map.has(prop)) map.set(prop, undefined);
  }
  return map;
}

function ensureAdapter(self) {
  if (self._adapter) return self._adapter;

  if (HAS_AFRAME && (self.el || self.__el)) {
    Object.defineProperty(self, "_adapter", { value: new SnapColliderAFrameAdapter(self), configurable: true });
    return self._adapter;
  }

  const descriptors = collectDescriptorsFor(self);
  Object.defineProperty(self, "_adapter", {
    value: new SnapColliderEditorAdapter(self, descriptors),
    configurable: true
  });
  return self._adapter;
}

export class SnapColliderElement extends ScriptableObject {
  static scriptableType = "snap-collider";

  constructor(initial = {}) {
    super();

    if (HAS_AFRAME) {
      const el = document.createElement("a-entity");
      // The presence of the snap-collider component is all that's needed
      el.setAttribute("snap-collider", "");

      const obj = this.constructor.adopt(el); // identity-preserving prototype shift
      if (Object.getPrototypeOf(obj) !== this.constructor.prototype) {
        Object.setPrototypeOf(obj, this.constructor.prototype);
      }

      // lazy adapter + apply props (if any)
      ensureAdapter(obj);

      // CRITICAL ECS INITIALIZATION PRESERVED
      if (typeof ScriptableObject.snapColliderEcsInit === "function") {
        try {
          ScriptableObject.snapColliderEcsInit(el, initial);
        } catch (e) {
          console.warn("SnapColliderElement: Failed to initialise ECS entity", e);
        }
      }

      // Apply initial properties
      for (const k of Object.keys(initial)) {
        if (k in obj) obj[k] = initial[k];
      }

      return obj;
    }

    // editor / plain Three
    if (!this.scriptableType) {
      Object.defineProperty(this, "scriptableType", { value: this.constructor.scriptableType, configurable: true });
    }

    const node = createSnapColliderElementEditor(initial);
    ensureAdapter(node);

    for (const k of Object.keys(initial)) {
      if (k in node) node[k] = initial[k];
    }

    return node;
  }

  copy(source, recursive = true) {
    if (!source || !source.isScriptableObject) return this;

    // 1) copy transform only (no children)
    THREE.Object3D.prototype.copy.call(this, source, /* recursive = */ false);

    // 2) copy public snap collider props via adapter (deep where needed)
    const take = (prop, val) => {
      if (val === undefined) return;
      if (Array.isArray(val)) {
        this[prop] = val.slice();
      } else if (val && typeof val.clone === "function") {
        this[prop] = val.clone();
      } else {
        this[prop] = val;
      }
    };

    for (const p of SNAP_PROPS) {
      take(p, source[p]);
    }

    // 3) Don't clear existing children on A-Frame context - let the new SnapColliderElement keep its ECS-created entities
    // This ensures functionality (clickable, networked, physics) is preserved
    if (!HAS_AFRAME) {
      // Clear existing children for editor mode
      for (const c of [...this.children]) {
        this.remove(c);
      }
    }

    // 4) Copy children from source
    // WARNING: This will create duplicate infrastructure children (guides, ECS entities)
    // Each SnapColliderElement needs its own ECS infrastructure to work properly (networking, physics, interaction)
    // The duplication is intentional because we don't have a reliable way to identify the children that are ECS-created - each clone gets both:
    //   - Its own functional ECS infrastructure (created during construction)
    //   - Copies of all source children (including source's infrastructure)
    // This ensures the clone works properly even though it has some duplicate infrastructure objects.
    if (recursive) {
      const sourceChildren = logicalChildrenOf(source);

      for (const srcChild of sourceChildren) {
        const clonedChild = srcChild.clone(true);
        this.add(clonedChild);
      }

      // Ensure matrix world is updated after adding children
      this.updateMatrixWorld(true);
    }

    return this;
  }

  clone(recursive = true) {
    // Build init payload so the new element is born with correct component state
    const init = {};
    for (const p of SNAP_PROPS) {
      const v = this[p];
      if (v === undefined) continue;

      if (Array.isArray(v)) {
        init[p] = v.slice();
      } else if (v && typeof v.clone === "function") {
        init[p] = v.clone();
      } else {
        init[p] = v;
      }
    }

    // Prefer factory if available (handles adopt/constructor nuances)
    const Ctor =
      this.constructor && typeof this.constructor.create === "function" ? this.constructor : SnapColliderElement;

    const dest = typeof Ctor.create === "function" ? Ctor.create(init) : new SnapColliderElement(init);

    // Write object3D transforms (component sync for A-Frame to be added later)
    dest.position.copy(this.position);
    dest.quaternion.copy(this.quaternion);
    dest.scale.copy(this.scale);

    // Copy remaining fields & children
    return dest.copy(this, recursive);
  }

  static create(initial = {}) {
    return new this(initial);
  }
}

// DRY helper: upgrade a plain Editor SnapColliderNode instance to Scriptable, attach adapter and proxies
function makeEditorSnapColliderNodeScriptable(node) {
  if (!node) return node;
  if (node.isScriptableObject === true) return node;

  // Preserve original chain and add Scriptable API on top
  const originalProto = Object.getPrototypeOf(node);
  const ScriptableClass = ScriptableMixin(
    (originalProto && originalProto.constructor) || SnapColliderCtor || THREE.Object3D
  );
  if (Object.getPrototypeOf(ScriptableClass.prototype) !== originalProto) {
    Object.setPrototypeOf(ScriptableClass.prototype, originalProto);
  }
  Object.setPrototypeOf(node, ScriptableClass.prototype);

  // Annotate type
  try {
    if (!node.scriptableType)
      Object.defineProperty(node, "scriptableType", { value: SnapColliderElement.scriptableType, configurable: true });
  } catch (_) {
    node.scriptableType = SnapColliderElement.scriptableType;
  }

  // Attach adapter and per-instance property proxies
  const descriptors = collectDescriptorsFor(node);
  try {
    Object.defineProperty(node, "_adapter", {
      value: new SnapColliderEditorAdapter(node, descriptors),
      configurable: true
    });
  } catch (_) {
    node._adapter = new SnapColliderEditorAdapter(node, descriptors);
  }

  for (const prop of SNAP_PROPS) {
    try {
      Object.defineProperty(node, prop, {
        get() {
          return this._adapter?.[prop];
        },
        set(v) {
          if (this._adapter) this._adapter[prop] = v;
        },
        enumerable: true,
        configurable: true
      });
    } catch (_) {
      /* ignore define errors */
    }
  }

  return node;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Bind property surface (works for adopted instances too)                    */
/* ────────────────────────────────────────────────────────────────────────── */
SNAP_PROPS.forEach(prop => {
  Object.defineProperty(SnapColliderElement.prototype, prop, {
    get() {
      return ensureAdapter(this)?.[prop];
    },
    set(v) {
      const a = ensureAdapter(this);
      if (a) a[prop] = v;
    },
    enumerable: true,
    configurable: true
  });
});

/* ────────────────────────────────────────────────────────────────────────── */
/* Optional editor factory helper                                             */
/* ────────────────────────────────────────────────────────────────────────── */
export function createSnapColliderElementEditor(initial = {}) {
  const SnapColliderCtor = globalThis.SnapColliderNode;

  if (typeof SnapColliderCtor !== "function") {
    throw new Error("SnapColliderElementEditor: SnapColliderNode constructor not found.");
  }
  let node;
  try {
    const editor = ScriptableObject.editor || globalThis.editor;
    node = new SnapColliderCtor(editor);
  } catch (e) {
    throw new Error(`SnapColliderElementEditor: failed to construct – ${e?.message || e}`);
  }

  makeEditorSnapColliderNodeScriptable(node);

  for (const k of Object.keys(initial)) {
    if (k in node) node[k] = initial[k];
  }
  return node;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Registry: promote A-Frame elements and pure-Three objects                  */
/* ────────────────────────────────────────────────────────────────────────── */
ScriptableObjects.registerType("snap-collider", source => {
  if (!source) return source;

  const type = source.scriptableType || source.object3D?.scriptableType || source?.__el?.object3D?.scriptableType || source?.el?.object3D?.scriptableType;

  if (type !== SnapColliderElement.scriptableType) return source;

  // A-Frame entities: use base adopt via class method
  if (isAFrameEntity(source)) return SnapColliderElement.adopt(source);

  // Editor/plain-Three nodes: upgrade in-place via shared helper
  if (isObject3D(source)) return makeEditorSnapColliderNodeScriptable(source);

  return source;
});

/* Optional: nicer DevTools label */
Object.defineProperty(SnapColliderElement.prototype, Symbol.toStringTag, { value: "SnapColliderElement" });
